<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';

import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import IconCheck from '@/shared/components/UI/Icons/IconCheck.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { refreshBingoIdentity } from '@/shared/composables/bingo/refresh-identity';
import { useMyProfile } from '@/shared/composables/user/myProfile';
import {
  toCraftSelections,
  toCraftSkillsInput,
  useUpdateMyProfile
} from '@/shared/composables/user/updateMyProfile';
import { useTranslations } from '@/shared/composables/useTranslations';
import {
  postSignupDetailsModal,
  userDetailsFormSources
} from '@/shared/constants/constants';
import { useUserStore } from '@/shared/pinia/modules/user';
import {
  trackUserDetailsFormFieldFilled,
  trackUserDetailsFormShown,
  trackUserDetailsFormSubmitted,
  type UserDetailsFormField
} from '@/shared/tracking/user-details-form';
import { hobbiiSessionStorage } from '@/shared/utils/web-storage';

import ProfileDetailsFields from './ProfileDetailsFields.vue';

const { t } = useTranslations();
const userStore = useUserStore();

const { profile, missingFields, refetch } = useMyProfile();
const { updateMyProfile, loading } = useUpdateMyProfile();

// Read once: a fresh page load re-mounts this component anyway on this
// multi-page theme. window.Shopify.routes.root carries the active market
// prefix (e.g. "/en-ca/"), so it has to be part of the comparison -- see
// getAccountBase() in accountUrls.ts for the same pattern.
const isBingoPage =
  window.location.pathname ===
  `${window.Shopify.routes.root}${postSignupDetailsModal.BINGO_PATH_SEGMENT}`;

// On Bingo, there's no way to play without a name, so the modal is
// mandatory and reappears on every visit. Everywhere else it's a one-time
// ask per session: dismissing (or completing) it once is enough.
const dismissedThisSession = ref(
  hobbiiSessionStorage.getItem(postSignupDetailsModal.DISMISSED_SESSION_KEY) ===
    'true'
);

const dismissForSession = () => {
  dismissedThisSession.value = true;
  hobbiiSessionStorage.setItem(
    postSignupDetailsModal.DISMISSED_SESSION_KEY,
    'true'
  );
};

// The trigger is a missing first name specifically -- missing last name or
// craft/skill alone doesn't open this (the account-page banner/modal from
// CE-632 covers those; forcing this mandatory Bingo-gate modal open over a
// last name would risk it never closing for someone who's happy leaving
// that blank). missingFields stays null until the profile query resolves,
// so this defaults closed rather than flashing open before we know.
const active = computed(() => {
  if (!userStore.isLogged || !(missingFields.value?.firstName ?? false)) {
    return false;
  }
  return isBingoPage || !dismissedThisSession.value;
});

const onUpdateActive = (value: boolean) => {
  if (!value) dismissForSession();
};

const onMaybeLater = () => dismissForSession();

watch(active, (isActive) => {
  if (isActive) {
    trackUserDetailsFormShown(userDetailsFormSources.POST_SIGNUP, {
      bingo_mandatory: isBingoPage
    });
  }
});

const onFieldFilled = (field: UserDetailsFormField) => {
  trackUserDetailsFormFieldFilled(field, userDetailsFormSources.POST_SIGNUP, {
    bingo_mandatory: isBingoPage
  });
};

// Craft/skill are asked here too when missing, since a customer who already
// gave them on the pattern download form shouldn't be asked twice.
const showCraftSkill = computed(
  () => missingFields.value?.craftSkills ?? false
);

// Bingo can't be played without a name, so its copy leads with that instead
// of the generic personalisation pitch.
const modalTitle = computed(() =>
  t(
    isBingoPage
      ? 'account.post_signup_details_modal.bingo.title'
      : 'account.post_signup_details_modal.title'
  )
);
const modalDescription = computed(() =>
  t(
    isBingoPage
      ? 'account.post_signup_details_modal.bingo.description'
      : 'account.post_signup_details_modal.description'
  )
);

// Pre-filled in case only craft/skill is missing -- the trigger above only
// requires firstName to be blank.
const firstName = ref(profile.value?.first_name ?? '');
const initialCrafts = computed(() =>
  toCraftSelections(profile.value?.craft_skills ?? [])
);
const submitError = ref<string | null>(null);

const fields = useTemplateRef('fields');

const onFormSubmit = async () => {
  submitError.value = null;
  trackUserDetailsFormSubmitted(userDetailsFormSources.POST_SIGNUP, {
    bingo_mandatory: isBingoPage
  });

  try {
    await updateMyProfile({
      first_name: firstName.value.trim(),
      ...(showCraftSkill.value && fields.value
        ? { craft_skills: toCraftSkillsInput(fields.value.crafts) }
        : {})
    });
    // Re-reads myProfile so `active` (driven by missingFields) closes this
    // modal on its own once the save is confirmed. dismissForSession is a
    // belt-and-braces backstop for the non-Bingo case, in case the refetch
    // is slow to land.
    dismissForSession();
    await refetch();
    await refreshBingoIdentity().catch(() => false);
  } catch {
    submitError.value = t('account.post_signup_details_modal.error');
  }
};
</script>

<template>
  <!-- close-on-click-outside stays off everywhere, not just on Bingo:
  useModal's outside-click detection only ignores elements with
  role="dialog", so an unrelated overlay without that role (the
  Usercentrics consent banner, for one) would otherwise dismiss this on a
  click that has nothing to do with it. The close button and Escape key
  are deliberate actions instead. -->
  <Modal
    :active="active"
    size="md"
    :close-button="!isBingoPage"
    :close-on-escape-key="!isBingoPage"
    :close-on-click-outside="false"
    :footer-divider="false"
    padded
    @update:active="onUpdateActive"
  >
    <template #header>
      <div class="flex w-full flex-col items-center gap-2 text-black">
        <h2 class="text-center text-2xl font-bold leading-8">
          {{ modalTitle }}
        </h2>
        <p class="text-pretty text-center text-base leading-6">
          {{ modalDescription }}
        </p>
      </div>
    </template>
    <template #body>
      <form
        class="flex w-full flex-col gap-6 text-black"
        @submit.prevent="onFormSubmit"
      >
        <ProfileDetailsFields
          ref="fields"
          v-model:first-name="firstName"
          :show-craft-skill="showCraftSkill"
          :show-last-name="false"
          :initial-crafts="initialCrafts"
          @field-filled="onFieldFilled"
        />

        <div v-if="submitError" class="text-error">{{ submitError }}</div>

        <ButtonText
          type="submit"
          :disabled="!fields?.isValid || loading"
          :loading="loading"
          theme="primary"
          size="lg"
          full-width
          class="mt-4"
        >
          {{ t('account.post_signup_details_modal.submit_button') }}
          <template #icon>
            <IconCheck />
          </template>
        </ButtonText>

        <div v-if="!isBingoPage" class="-mt-2 text-center">
          <a
            href="#"
            class="text-sm font-bold text-primary hover:text-primary-600"
            @click.prevent="onMaybeLater"
            >{{ t('account.post_signup_details_modal.maybe_later') }}</a
          >
        </div>
      </form>
    </template>
    <template #footer>
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="text-center text-xs text-[#000] [&_a]:font-bold [&_a]:text-[#000]"
        v-html="t('account.post_signup_details_modal.privacy_notice')"
      ></div>
      <!-- eslint-enable vue/no-v-html -->
    </template>
  </Modal>
</template>
